import {get, set} from "../../../../../utils/dotutils";
import {operation} from "../../../../../utils/other";
import AbortablePromise from "../../../../../utils/promise/AbortablePromise";
import DataProviderComp from "./DataProviderComp";
import {FilterType} from "../Types/FilterType";
import {OrderType} from "../Types/OrderType";
import {RequestParams} from "../Types/RequestParams";
import {DataGridProps} from "../Types/DataGridProps";
import {ProviderProps} from "../Types/ProviderProps";
import {Items} from "../Types/Items";

export type ListDataProviderProps = DataGridProps & ProviderProps & {
    value: Record<string | number | symbol, any>[],
};

export default class ListDataProvider<Props extends ListDataProviderProps, State> extends DataProviderComp<Props, State> {

    static defaultProps = {
        selectorFunction: ({item, items, selection, props, selectionType = "index"}) => {
            switch (selectionType) {
                case "selectable":
                    return true;
                case "index":
                    // TODO
                    return -1;
                case "id":
                    // TODO
                    return null;
            }
        },
    }

    constructor(props: Props) {
        super(props);
        this.addItems = this.addItems.bind(this);
        this.editItems = this.editItems.bind(this);
        this.removeItems = this.removeItems.bind(this);
        this.parseView = this.parseView.bind(this);
        this.loadData = this.loadData.bind(this);
    }

    async addItems(items: Items): Promise<any> {
        items = await super.addItems(items);
        let newItems = [...(this.props.value || [])];
        if (this.props.addItems) {
            newItems = this.props.addItems(items, this.props);
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (this.props.saveChanges && newItems) {
            this.props.saveChanges(this.props.name, newItems);
        }
    }

    async editItems(items: Items): Promise<any> {
        items = await super.editItems(items);
        let newItems = [...(this.props.value || [])];
        if (this.props.editItems) {
            newItems = this.props.editItems(items, this.props);
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (this.props.saveChanges && newItems) {
            this.props.saveChanges(this.props.name, newItems);
        }
    }

    async removeItems(items: Items): Promise<any> {
        items = await super.removeItems(items);
        let newItems = [...(this.props.value || [])];
        if (this.props.removeItems) {
            newItems = this.props.removeItems(items, this.props);
        }
        if (this.props.onItemsChanged) {
            this.props.onItemsChanged();
        }
        if (this.props.saveChanges && newItems) {
            this.props.saveChanges(this.props.name, newItems);
        }
    }

    parseView(props: Props): object {
        let view = {};
        props.getFields().map((column) => set(view, column.props.field, ""));
        return view;
    }

    filterRow(filter: FilterType|FilterType[], row: object) {
        if (Array.isArray(filter)) {
            for (const subFilter of filter) {
                if (!this.filterRow(subFilter, row)) {
                    return false;
                }
            }
            return true;
        } else if (filter?.filters) {
            const operator = filter.operator || "AND";
            if (operator.trim().toUpperCase() === "OR") {
                for (const subFilter of filter.filters) {
                    if (this.filterRow(subFilter, row)) {
                        return true;
                    }
                }
                return false;
            } else if (operator.trim().toUpperCase() === "AND") {
                for (const subFilter of filter.filters) {
                    if (!this.filterRow(subFilter, row)) {
                        return false;
                    }
                }
                return true;
            }
        } else {
            if (filter.field) {
                const operator = (filter.operator || "=").toLowerCase();
                switch (operator) {
                    case "not isnull":
                        return get(row, filter.field) !== null && get(row, filter.field) !== undefined;
                    case "isnull":
                        return get(row, filter.field) === null || get(row, filter.field) === undefined;
                    case "!~":
                    case "~":
                    case "!=":
                    case "==":
                    case "<":
                    case ">":
                    case "<=":
                    case ">=":
                    case "===":
                    case "not in":
                    case "in":
                    case "includes":
                    case "not includes":
                        return operation(get(row, filter.field), filter.value, operator);
                    case "like":
                        // https://stackoverflow.com/a/37511463
                        let fieldValue = String(get(row, filter.field));
                        fieldValue = fieldValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                        let value = String(filter.value);
                        value = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                        const matchmode = filter.matchmode || "any";
                        switch (matchmode) {
                            case "start":
                                return String(fieldValue).indexOf(value) === 0;
                            case "end":
                                const s = String(fieldValue);
                                const suffix = String(value);
                                return s.indexOf(suffix, s.length - suffix.length) !== -1;
                            case "any":
                                return String(fieldValue).indexOf(value) !== -1;
                        }
                        return false;
                }
            }
            return true;
        }
    }

    filterData(filter: FilterType|FilterType[], data: object[]) {
        if (!filter) return data;
        return [...data].filter((row) => {
            return this.filterRow(filter, row);
        });
    }

    sortRow<ItemType extends any,>(order: OrderType, a: ItemType, b: ItemType) {
        let left = get(a, order.field);
        let right = get(b, order.field);
        const direction = order.direction.toUpperCase();
        if (typeof left === "string" && (right === null || right === undefined)) {
            right = "";
        }
        if (typeof right === "string" && (left === null || left === undefined)) {
            left = "";
        }
        if (typeof left === "string" && typeof right === "string") {
            if (left.length !== right.length) {
                left = left.length;
                right = right.length;
            }
        }
        if (direction === "DESC") {
            if (left > right) return -1;
            if (left < right) return +1;
        } else if (direction === "ASC") {
            if (left > right) return +1;
            if (left < right) return -1;
        }
        return 0;
    }

    sortRows<ItemType extends any, >(orders: OrderType[], rows: ItemType[]): ItemType[] {
        const groupBy = function (xs: any[], key: string): any[] {
            return xs.reduce(function (rv, x) {
                (rv[get(x, key)] = rv[get(x, key)] || []).push(x);
                return rv;
            }, {});
        };
        const order = orders.shift();
        if (!order) {
            return [...rows];
        }
        rows.sort((a, b) => this.sortRow<ItemType>(order, a, b));
        if (orders.length) {
            const groups = groupBy(rows, order.field);
            return Object.values(groups).map<ItemType[]>((group: ItemType[]) => {
                group.sort((a, b) => this.sortRow<ItemType>(order, a, b));
                return this.sortRows([...orders], group);
            }).reduce<ItemType[]>((all: ItemType[], current) => ([...all, ...current]), []);
        } else {
            return [...rows];
        }
    }

    sortData(order: OrderType|OrderType[], data: object[]) {
        if (!order) return data;
        data = [...data];
        if (Array.isArray(order)) {
            let orders = [...order];
            data = this.sortRows(orders, data);
            return data;
        } else if (order) {
            data = [...data];
            data.sort((a, b) => this.sortRow(order, a, b));
            return data;
        }
        return data;
    }

    loadData(request: RequestParams): AbortablePromise<ResponseData> {
        return new AbortablePromise<ResponseData>(async (resolve, reject, signal) => {
            const originalData = this.props.value || [];
            let data: Record<string|number|symbol, any>[] = [...originalData].map((data, idx) => {
                data[this.rowKeyField] = idx;
                return data;
            });
            data = this.filterData(request.filter, data);
            data = this.sortData(request.order, data);
            const pageSize = request.itemsperpage > 0 ? request.itemsperpage : data.length;
            const rangestart = ((request.page - 1) * pageSize) + 1;
            const rangeend = Math.min((request.page - 1) * pageSize + pageSize, data.length);
            const items = data.slice(rangestart-1, rangeend);

            let response: ResponseData = {
                fullcount: data.length,
                caption: null,
                navigation: {
                    currpage: request.page,
                    link: null,
                    pagecount: Math.ceil(data.length / pageSize),
                    rangestart,
                    rangeend
                },
                page: request.page,
                itemcount: pageSize,
                itemsperpage: pageSize,
                items,
                columns: {},
                aggregations: null,
                groupAggregations: null
            };

            if (this.props.onLoadCompleted) {
                const formattedData = await this.props.onLoadCompleted(response, request);
                response = formattedData || response;
            }

            resolve(response);
        });
    }

}
